import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  Plus,
  Loader2,
  Calendar,
  FileText,
  User,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARTIFACT_TYPES = [
  { value: "audio", label: "Audio Recordings" },
  { value: "transcript", label: "Transcripts" },
  { value: "minutes", label: "Minutes" },
  { value: "attachments", label: "Attachments" },
];

export default function LegalHoldsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [legalHolds, setLegalHolds] = useState([]);
  const [meetings, setMeetings] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedHold, setSelectedHold] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // New legal hold form
  const [newHold, setNewHold] = useState({
    reason: "",
    notes: "",
    scope: {
      meeting_ids: [],
      date_range_start: "",
      date_range_end: "",
      artifact_types: [],
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [holdsRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/company/legal-holds`),
        axios.get(`${API}/meetings`).catch(() => ({ data: { meetings: [] } })),
      ]);
      setLegalHolds(holdsRes.data.legal_holds || []);
      setMeetings(meetingsRes.data.meetings || []);
    } catch (error) {
      toast.error("Failed to load legal holds");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLegalHold = async (e) => {
    e.preventDefault();
    
    if (!newHold.reason.trim()) {
      toast.error("Please provide a reason for the legal hold");
      return;
    }
    
    if (newHold.scope.meeting_ids.length === 0 && 
        !newHold.scope.date_range_start && 
        !newHold.scope.date_range_end && 
        newHold.scope.artifact_types.length === 0) {
      toast.error("Please define at least one scope criteria");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/company/legal-holds`, newHold);
      toast.success("Legal hold created successfully");
      setShowCreateModal(false);
      setNewHold({
        reason: "",
        notes: "",
        scope: {
          meeting_ids: [],
          date_range_start: "",
          date_range_end: "",
          artifact_types: [],
        },
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create legal hold");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivateLegalHold = async () => {
    if (!selectedHold) return;
    
    setDeactivating(true);
    try {
      await axios.post(`${API}/company/legal-holds/${selectedHold.id}/deactivate`);
      toast.success("Legal hold deactivated");
      setShowDeactivateDialog(false);
      setSelectedHold(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate legal hold");
    } finally {
      setDeactivating(false);
    }
  };

  const toggleMeetingSelection = (meetingId) => {
    const currentIds = newHold.scope.meeting_ids || [];
    if (currentIds.includes(meetingId)) {
      setNewHold({
        ...newHold,
        scope: {
          ...newHold.scope,
          meeting_ids: currentIds.filter(id => id !== meetingId),
        },
      });
    } else {
      setNewHold({
        ...newHold,
        scope: {
          ...newHold.scope,
          meeting_ids: [...currentIds, meetingId],
        },
      });
    }
  };

  const toggleArtifactType = (type) => {
    const currentTypes = newHold.scope.artifact_types || [];
    if (currentTypes.includes(type)) {
      setNewHold({
        ...newHold,
        scope: {
          ...newHold.scope,
          artifact_types: currentTypes.filter(t => t !== type),
        },
      });
    } else {
      setNewHold({
        ...newHold,
        scope: {
          ...newHold.scope,
          artifact_types: [...currentTypes, type],
        },
      });
    }
  };

  const getMeetingName = (meetingId) => {
    const meeting = meetings.find(m => m.id === meetingId);
    return meeting?.name || meetingId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-stone-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-semibold text-stone-900">
                    Legal Holds
                  </h1>
                  <p className="text-xs text-stone-500">
                    Manage data preservation
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="create-legal-hold-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Legal Hold
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">About Legal Holds</p>
            <p className="text-sm text-amber-700 mt-1">
              Legal holds preserve data that may be relevant to litigation or regulatory matters.
              When a legal hold is active, affected artifacts cannot be deleted or modified.
            </p>
          </div>
        </div>

        {/* Legal Holds List */}
        <div className="space-y-4" data-testid="legal-holds-list">
          {legalHolds.length === 0 ? (
            <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
              <Lock className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500 font-medium">No Legal Holds</p>
              <p className="text-sm text-stone-400 mt-1">Create a legal hold to preserve important data</p>
            </div>
          ) : (
            legalHolds.map((hold) => (
              <div
                key={hold.id}
                className={`bg-white rounded-lg border p-6 ${
                  hold.is_active ? "border-red-200" : "border-stone-200"
                }`}
                data-testid={`legal-hold-${hold.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      hold.is_active ? "bg-red-100" : "bg-stone-100"
                    }`}>
                      <Lock className={`w-6 h-6 ${hold.is_active ? "text-red-600" : "text-stone-400"}`} />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-stone-900">{hold.reason}</h3>
                        <Badge variant={hold.is_active ? "destructive" : "secondary"}>
                          {hold.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      
                      {hold.notes && (
                        <p className="text-sm text-stone-500 mt-1">{hold.notes}</p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {hold.created_by_name || "Unknown"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(hold.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {/* Scope Details */}
                      <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                        <p className="text-xs font-medium text-stone-600 mb-2">Scope</p>
                        <div className="space-y-1 text-sm">
                          {hold.scope?.meeting_ids?.length > 0 && (
                            <p className="text-stone-600">
                              <span className="text-stone-500">Meetings:</span>{" "}
                              {hold.scope.meeting_ids.map(id => getMeetingName(id)).join(", ")}
                            </p>
                          )}
                          {hold.scope?.date_range_start && (
                            <p className="text-stone-600">
                              <span className="text-stone-500">Date Range:</span>{" "}
                              {hold.scope.date_range_start} to {hold.scope.date_range_end || "present"}
                            </p>
                          )}
                          {hold.scope?.artifact_types?.length > 0 && (
                            <p className="text-stone-600">
                              <span className="text-stone-500">Artifact Types:</span>{" "}
                              {hold.scope.artifact_types.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {hold.deactivated_at && (
                        <p className="text-xs text-stone-400 mt-2">
                          Deactivated on {new Date(hold.deactivated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {hold.is_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedHold(hold);
                        setShowDeactivateDialog(true);
                      }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`deactivate-hold-${hold.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Create Legal Hold Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Create Legal Hold
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateLegalHold} className="space-y-6 mt-4">
            <div>
              <Label>Reason for Legal Hold *</Label>
              <Input
                value={newHold.reason}
                onChange={(e) => setNewHold({ ...newHold, reason: e.target.value })}
                placeholder="e.g., Litigation - Smith v. Company"
                required
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newHold.notes}
                onChange={(e) => setNewHold({ ...newHold, notes: e.target.value })}
                placeholder="Additional details about the legal hold..."
              />
            </div>
            
            {/* Scope Section */}
            <div className="space-y-4 p-4 border border-stone-200 rounded-lg">
              <h3 className="font-medium text-stone-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-stone-500" />
                Define Scope
              </h3>
              
              {/* Meetings Selection */}
              <div>
                <Label className="text-sm text-stone-600">Select Meetings</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border border-stone-200 rounded-lg divide-y">
                  {meetings.length > 0 ? meetings.slice(0, 20).map((meeting) => (
                    <label
                      key={meeting.id}
                      className="flex items-center gap-3 p-3 hover:bg-stone-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={newHold.scope.meeting_ids?.includes(meeting.id)}
                        onCheckedChange={() => toggleMeetingSelection(meeting.id)}
                      />
                      <div>
                        <p className="text-sm font-medium text-stone-700">{meeting.name}</p>
                        <p className="text-xs text-stone-500">{meeting.date}</p>
                      </div>
                    </label>
                  )) : (
                    <p className="p-3 text-sm text-stone-500">No meetings available</p>
                  )}
                </div>
              </div>
              
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-stone-600">Date Range Start</Label>
                  <Input
                    type="date"
                    value={newHold.scope.date_range_start}
                    onChange={(e) => setNewHold({
                      ...newHold,
                      scope: { ...newHold.scope, date_range_start: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label className="text-sm text-stone-600">Date Range End</Label>
                  <Input
                    type="date"
                    value={newHold.scope.date_range_end}
                    onChange={(e) => setNewHold({
                      ...newHold,
                      scope: { ...newHold.scope, date_range_end: e.target.value }
                    })}
                  />
                </div>
              </div>
              
              {/* Artifact Types */}
              <div>
                <Label className="text-sm text-stone-600">Artifact Types</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ARTIFACT_TYPES.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        newHold.scope.artifact_types?.includes(type.value)
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      <Checkbox
                        checked={newHold.scope.artifact_types?.includes(type.value)}
                        onCheckedChange={() => toggleArtifactType(type.value)}
                        className="sr-only"
                      />
                      <FileText className="w-4 h-4" />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="bg-red-600 hover:bg-red-700"
              >
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Legal Hold
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Deactivate Legal Hold?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will release the legal hold on the affected artifacts. They will no longer be 
              protected from deletion or modification. This action is logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateLegalHold}
              disabled={deactivating}
              className="bg-red-600 hover:bg-red-700"
            >
              {deactivating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
